
import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { ApiProvider, useCreateApiProvider, useUpdateApiProvider } from '@/hooks/useApiProviders';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Loader2 } from 'lucide-react';

interface ProviderFormProps {
  provider?: ApiProvider;
  onSuccess?: () => void;
}

const formSchema = z.object({
  name: z.string().min(2, { message: 'Name must be at least 2 characters' }),
  code: z.string().min(2, { message: 'Code must be at least 2 characters' }),
  base_url: z.string().url({ message: 'Must be a valid URL' }),
  auth_type: z.enum(['oauth2', 'api_key', 'custom_token']),
  auth_url: z.string().url({ message: 'Must be a valid URL' }).optional().or(z.literal('')),
  token_url: z.string().url({ message: 'Must be a valid URL' }).optional().or(z.literal('')),
  client_id: z.string().optional().or(z.literal('')),
  client_secret: z.string().optional().or(z.literal('')),
  scope: z.string().optional().or(z.literal('')),
  is_active: z.boolean().default(true),
  is_default: z.boolean().default(false),
  logo_url: z.string().url({ message: 'Must be a valid URL' }).optional().or(z.literal('')),
});

export function ProviderForm({ provider, onSuccess }: ProviderFormProps) {
  const { mutate: createProvider, isPending: isCreating } = useCreateApiProvider();
  const { mutate: updateProvider, isPending: isUpdating } = useUpdateApiProvider();
  
  const isPending = isCreating || isUpdating;
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: provider ? {
      ...provider,
      auth_url: provider.auth_url || '',
      token_url: provider.token_url || '',
      client_id: provider.client_id || '',
      client_secret: provider.client_secret || '',
      scope: provider.scope || '',
      logo_url: provider.logo_url || '',
      is_active: provider.is_active !== false,
      is_default: provider.is_default === true,
    } : {
      name: '',
      code: '',
      base_url: '',
      auth_type: 'oauth2',
      auth_url: '',
      token_url: '',
      client_id: '',
      client_secret: '',
      scope: '',
      logo_url: '',
      is_active: true,
      is_default: false,
    },
  });

  const authType = form.watch('auth_type');

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    // Ensure all required fields are present
    const submissionData: Omit<ApiProvider, 'id'> = {
      name: data.name,
      code: data.code,
      base_url: data.base_url,
      auth_type: data.auth_type,
      auth_url: data.auth_url || undefined,
      token_url: data.token_url || undefined,
      client_id: data.client_id || undefined,
      client_secret: data.client_secret || undefined,
      scope: data.scope || undefined,
      logo_url: data.logo_url || undefined,
      is_active: data.is_active,
      is_default: data.is_default
    };
    
    if (provider) {
      updateProvider({
        id: provider.id,
        ...submissionData,
      }, {
        onSuccess: () => {
          if (onSuccess) onSuccess();
        },
      });
    } else {
      createProvider(submissionData, {
        onSuccess: () => {
          if (onSuccess) onSuccess();
        },
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Provider Name</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. 24Seven Office" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Provider Code</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. 24seven" {...field} />
                </FormControl>
                <FormDescription>
                  Unique identifier used in code
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <FormField
          control={form.control}
          name="base_url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Base API URL</FormLabel>
              <FormControl>
                <Input placeholder="https://api.example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="auth_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Authentication Type</FormLabel>
              <Select 
                onValueChange={field.onChange} 
                defaultValue={field.value}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select auth type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="oauth2">OAuth 2.0</SelectItem>
                  <SelectItem value="api_key">API Key</SelectItem>
                  <SelectItem value="custom_token">Custom Token</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {(authType === 'oauth2' || authType === 'custom_token') && (
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
            <FormField
              control={form.control}
              name="auth_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Authentication URL</FormLabel>
                  <FormControl>
                    <Input placeholder="https://auth.example.com/oauth" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="token_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Token URL</FormLabel>
                  <FormControl>
                    <Input placeholder="https://auth.example.com/token" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
        
        {authType === 'oauth2' && (
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
            <FormField
              control={form.control}
              name="client_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Client ID</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="client_secret"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Client Secret</FormLabel>
                  <FormControl>
                    <Input type="password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="scope"
              render={({ field }) => (
                <FormItem className="col-span-2">
                  <FormLabel>Scope (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. read write" {...field} />
                  </FormControl>
                  <FormDescription>
                    Space-separated list of permissions
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
        
        <FormField
          control={form.control}
          name="logo_url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Logo URL (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="https://example.com/logo.png" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          <FormField
            control={form.control}
            name="is_active"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                <div className="space-y-0.5">
                  <FormLabel>Active</FormLabel>
                  <FormDescription>
                    Allow users to connect to this provider
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="is_default"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                <div className="space-y-0.5">
                  <FormLabel>Default Provider</FormLabel>
                  <FormDescription>
                    Make this the default provider for this type
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        
        <div className="flex justify-end gap-2 pt-4">
          <Button type="submit" disabled={isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {provider ? 'Update Provider' : 'Create Provider'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
